import { Fragment, useState } from "react";
import {
  Inbox,
  Loader2,
  PauseCircle,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierCancellation,
  type CarrierCancellationReturnOption,
  useCarrierCancellationReturnOptions,
  useCarrierCancellations,
  useCarrierDispatchCenters,
  useCarrierMe,
  useProcessCarrierCancellation,
} from "@/api/carrier";
import { TablePager, usePagedState } from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ApproveRefundLineSheet,
  type RefundLineApproval,
} from "@/features/approve-refund-line-sheet";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate, formatMoney } from "@/lib/format";

function customerName(row: CarrierCancellation): string {
  const customer = row.order?.placerCustomer;
  return customer
    ? [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
        customer.email
    : "-";
}

function refundAmount(row: CarrierCancellation): number {
  return (
    row.pendingProviderRefundLine?.amountGrossCents ??
    row.refundInfo?.totalRefund ??
    row.refundInfo?.refundAmountCents ??
    0
  );
}

function cancellationReason(row: CarrierCancellation): string {
  return (
    row.cancellationRequestReason ??
    row.cancellationReason ??
    row.pendingProviderRefundLine?.reason ??
    "-"
  );
}

function approvalFor(row: CarrierCancellation): RefundLineApproval | null {
  const line = row.pendingProviderRefundLine;
  if (!line) return null;
  return {
    refundRequestId: line.refundRequestId,
    lineId: line.id,
    shipmentId: row.id,
    shipmentLabel: `SHP-${row.shipmentNumber}`,
    chargeType: line.chargeType,
    amountNetCents: line.amountNetCents,
    taxCents: line.taxCents,
    amountGrossCents: line.amountGrossCents,
    createdAt: line.createdAt,
    source: line.source,
    reason: line.reason,
  };
}

function workContext(row: CarrierCancellation): string {
  const tasks = row.tasks ?? [];
  const trips = new Set(
    tasks.flatMap((task) => (task.tripId ? [task.tripId] : []))
  );
  const stops = new Set(
    tasks.flatMap((task) => (task.waypointId ? [task.waypointId] : []))
  );
  return `${tasks.length} task${tasks.length === 1 ? "" : "s"} · ${trips.size} trip${trips.size === 1 ? "" : "s"} · ${stops.size} stop${stops.size === 1 ? "" : "s"}`;
}

function CancellationDetail({
  row,
  onOpenChange,
  onApprove,
  canResolve,
}: {
  row: CarrierCancellation | null;
  onOpenChange: (open: boolean) => void;
  onApprove: (approval: RefundLineApproval) => void;
  canResolve: boolean;
}) {
  const line = row?.pendingProviderRefundLine;
  const processCancellation = useProcessCarrierCancellation();
  const quoteReturns = useCarrierCancellationReturnOptions();
  const { data: centers = [] } = useCarrierDispatchCenters();
  const [approving, setApproving] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [holdCenterId, setHoldCenterId] = useState(() => {
    const custody = row?.custody;
    return custody?.requiresDisposition &&
      custody.custody.kind === "COMPLETED_TASK"
      ? (custody.custody.dispatchCenterId ?? "")
      : "";
  });
  const [returnOptions, setReturnOptions] = useState<
    CarrierCancellationReturnOption[]
  >([]);
  const [returnTransportId, setReturnTransportId] = useState("");
  const [payer, setPayer] = useState<"CUSTOMER" | "CARRIER">("CUSTOMER");
  const [externalReference, setExternalReference] = useState("");

  const requiresDisposition = row?.custody?.requiresDisposition === true;
  const statusOnlyCustody =
    row?.custody?.requiresDisposition === true &&
    row.custody.custody.kind === "SHIPMENT_STATUS";
  const selectedReturn = returnOptions.find(
    (option) => option.returnTransportId === returnTransportId
  );
  const grossRefund = row ? refundAmount(row) : 0;
  const customerReturnCost =
    payer === "CUSTOMER" ? (selectedReturn?.returnChargeCents ?? 0) : 0;
  const netRefund = Math.max(0, grossRefund - customerReturnCost);
  const returnCenters = centers.filter(
    (center) => center.isActive && center.canHandleReturn
  );

  const submit = async (action: "resume" | "approve") => {
    if (!row) return;
    const reason =
      action === "resume"
        ? "Cancellation request declined"
        : cancellationReason(row) === "-"
          ? "Cancellation approved"
          : cancellationReason(row);
    const input: Parameters<typeof processCancellation.mutateAsync>[0] = {
      shipmentId: row.id,
      action,
      reason,
    };
    if (action === "approve" && requiresDisposition) {
      if (disposition === "HOLD_AT_DEPOT" && holdCenterId) {
        input.disposition = {
          type: "HOLD_AT_DEPOT",
          dispatchCenterId: holdCenterId,
          externalInterceptReference: externalReference.trim() || undefined,
        };
      } else if (disposition === "RETURN_TO_SENDER" && selectedReturn) {
        input.disposition = {
          type: "RETURN_TO_SENDER",
          returnTransportId: selectedReturn.returnTransportId,
          expectedRequestFingerprint: selectedReturn.requestFingerprint,
          payer,
          externalInterceptReference: externalReference.trim() || undefined,
        };
      } else {
        toast.error("Choose what happens to the parcel in custody");
        return;
      }
      if (statusOnlyCustody && !externalReference.trim()) {
        toast.error("Record the external intercept reference");
        return;
      }
    }
    try {
      const result = await processCancellation.mutateAsync(input);
      toast.success(
        action === "resume" ? "Service resumed" : "Cancellation approved",
        {
          description: result.message,
        }
      );
      onOpenChange(false);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      toast.error("Could not resolve cancellation", {
        description:
          status === 409
            ? "The shipment changed. The review queue and dispatch were refreshed."
            : "Try again.",
      });
    }
  };

  const priceReturn = async () => {
    if (!row) return;
    try {
      const result = await quoteReturns.mutateAsync(row.id);
      const options = result.kind === "ok" ? (result.data?.options ?? []) : [];
      setReturnOptions(options);
      setReturnTransportId(options[0]?.returnTransportId ?? "");
      if (options.length === 0) toast.error("No return services are available");
    } catch {
      toast.error("Could not price the return");
    }
  };

  return (
    <DetailDrawer
      open={row !== null}
      onOpenChange={onOpenChange}
      title={row ? `Cancellation · SHP-${row.shipmentNumber}` : ""}
      description={row ? customerName(row) : undefined}
      widthClassName="sm:max-w-xl"
      footer={
        row && line ? (
          <Button
            type="button"
            size="touch"
            className="w-full"
            onClick={() => {
              const approval = approvalFor(row);
              if (approval) onApprove(approval);
            }}
          >
            Review refund approval
          </Button>
        ) : row?.cancellationRequestedAt && canResolve ? (
          approving ? (
            <Button
              type="button"
              size="touch"
              className="w-full"
              disabled={processCancellation.isPending}
              onClick={() => void submit("approve")}
            >
              Confirm cancellation
            </Button>
          ) : (
            <div className="grid w-full grid-cols-2 gap-2">
              <Button
                type="button"
                size="touch"
                variant="outline"
                disabled={processCancellation.isPending}
                onClick={() => void submit("resume")}
              >
                Resume service
              </Button>
              <Button
                type="button"
                size="touch"
                onClick={() => setApproving(true)}
              >
                Approve cancellation
              </Button>
            </div>
          )
        ) : undefined
      }
    >
      {row && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-1">
            <StatusBadge status={line ? "REFUND_APPROVAL" : row.status} />
            <span className="rounded-full border px-2 py-1 text-[11px] font-semibold">
              {line
                ? "Stage 2 · Refund authorization"
                : "Stage 1 · Operational cancellation review"}
            </span>
          </div>
          {!line && row.cancellationRequestedAt && (
            <section className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <h3 className="flex items-center gap-2 font-semibold">
                <PauseCircle className="h-4 w-4" /> Cancellation review work
                hold
              </h3>
              <p className="mt-1 text-xs">
                Affected tasks stay visible, but assignment, start, completion,
                and failure actions are frozen. Unrelated trip work continues.
              </p>
              <p className="mt-2 font-medium">{workContext(row)}</p>
            </section>
          )}
          <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Shipment</dt>
            <dd className="font-mono text-xs">SHP-{row.shipmentNumber}</dd>
            <dt className="text-muted-foreground">Order</dt>
            <dd className="font-mono text-xs">
              {row.order ? `ORD-${row.order.orderNumber}` : "-"}
            </dd>
            <dt className="text-muted-foreground">Tracking</dt>
            <dd className="break-all font-mono text-xs">
              {row.trackingNumber ?? "-"}
            </dd>
            <dt className="text-muted-foreground">Customer</dt>
            <dd className="break-words">{customerName(row)}</dd>
            <dt className="text-muted-foreground">Customer email</dt>
            <dd className="break-all">
              {row.order?.placerCustomer?.email ?? "-"}
            </dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd>{row.status}</dd>
            <dt className="text-muted-foreground">Requested</dt>
            <dd>{formatDate(row.cancellationRequestedAt)}</dd>
            <dt className="text-muted-foreground">Reason</dt>
            <dd className="break-words">{cancellationReason(row)}</dd>
            <dt className="text-muted-foreground">Refund</dt>
            <dd>{formatMoney(refundAmount(row))} CAD</dd>
          </dl>

          {!line && row.cancellationRequestedAt && !canResolve && (
            <p className="rounded-md border bg-muted/40 p-3 text-sm">
              A carrier owner or dispatcher must resolve this cancellation
              review.
            </p>
          )}

          {!line && approving && requiresDisposition && (
            <section className="space-y-3 border-t pt-4">
              <h3 className="text-sm font-semibold">
                Parcel in carrier custody
              </h3>
              <NativeSelect
                aria-label="Physical custody disposition"
                value={disposition}
                onChange={(event) => setDisposition(event.target.value)}
              >
                <option value="">Choose disposition</option>
                <option value="RETURN_TO_SENDER">Return to sender</option>
                <option value="HOLD_AT_DEPOT">Hold at depot</option>
              </NativeSelect>
              {disposition === "HOLD_AT_DEPOT" && (
                <NativeSelect
                  aria-label="Return-capable dispatch center"
                  value={holdCenterId}
                  onChange={(event) => setHoldCenterId(event.target.value)}
                >
                  <option value="">Choose dispatch center</option>
                  {returnCenters.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
              {disposition === "RETURN_TO_SENDER" && (
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={quoteReturns.isPending}
                    onClick={() => void priceReturn()}
                  >
                    {quoteReturns.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Price return services
                  </Button>
                  {returnOptions.length > 0 && (
                    <>
                      <NativeSelect
                        aria-label="Return service"
                        value={returnTransportId}
                        onChange={(event) =>
                          setReturnTransportId(event.target.value)
                        }
                      >
                        {returnOptions.map((option) => (
                          <option
                            key={option.returnTransportId}
                            value={option.returnTransportId}
                          >
                            {option.carrier.name} · {option.carrierService.name}{" "}
                            · {formatMoney(option.returnChargeCents)}
                          </option>
                        ))}
                      </NativeSelect>
                      <NativeSelect
                        aria-label="Return cost paid by"
                        value={payer}
                        onChange={(event) =>
                          setPayer(event.target.value as "CUSTOMER" | "CARRIER")
                        }
                      >
                        <option value="CUSTOMER">Customer (default)</option>
                        <option value="CARRIER">Carrier</option>
                      </NativeSelect>
                      <div className="rounded-md bg-muted/50 p-3 text-sm">
                        <p>
                          Return cost:{" "}
                          {formatMoney(selectedReturn?.returnChargeCents ?? 0)}{" "}
                          CAD
                        </p>
                        <p className="font-semibold">
                          Customer refund after return: {formatMoney(netRefund)}{" "}
                          CAD
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          This approves operations only. Refund authorization
                          remains a separate queue stage.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
              {statusOnlyCustody && (
                <Input
                  aria-label="External intercept reference"
                  placeholder="External intercept reference"
                  value={externalReference}
                  onChange={(event) => setExternalReference(event.target.value)}
                />
              )}
            </section>
          )}

          {line && (
            <section className="space-y-3 border-t pt-4">
              <h3 className="text-sm font-semibold">Provider refund line</h3>
              <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
                {[
                  ["Charge", line.chargeType.replace(/_/g, " ")],
                  ["Net", formatMoney(line.amountNetCents)],
                  ["Tax", formatMoney(line.taxCents)],
                  ["Gross", formatMoney(line.amountGrossCents)],
                  ["Line status", line.status],
                  ["Source", line.source.replace(/_/g, " ")],
                  ["Created", formatDate(line.createdAt)],
                  ["Line reason", line.reason ?? "-"],
                ].map(([label, value]) => (
                  <Fragment key={label}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="break-words">{value}</dd>
                  </Fragment>
                ))}
              </dl>
            </section>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}

function QueueMessage({
  kind,
  onRetry,
}: {
  kind: "empty" | "error" | "forbidden";
  onRetry: () => void;
}) {
  const message =
    kind === "forbidden"
      ? "You do not have permission to view cancellations."
      : kind === "error"
        ? "Cancellations could not be loaded."
        : "No cancellations awaiting action.";
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      {kind === "empty" ? (
        <Inbox className="h-7 w-7 text-muted-foreground" />
      ) : (
        <TriangleAlert className="h-7 w-7 text-destructive" />
      )}
      <p role={kind === "empty" ? undefined : "alert"} className="text-sm">
        {message}
      </p>
      {kind === "error" && (
        <Button type="button" variant="outline" size="touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function CancellationsPanel() {
  const { page, pageSize, setPage, setPageSize } = usePagedState();
  const offset = (page - 1) * pageSize;
  const { data, isLoading, error, refetch } = useCarrierCancellations({
    limit: pageSize,
    offset,
  });
  const { data: carrierMe } = useCarrierMe();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [approval, setApproval] = useState<RefundLineApproval | null>(null);

  const rows = Array.isArray(data) ? data : (data?.data ?? []);
  const pagination = Array.isArray(data) ? null : data?.pagination;
  const detail = rows.find((row) => row.id === detailId) ?? null;
  const blockingError = error && rows.length === 0;
  const canResolve = Boolean(
    carrierMe?.roles?.some((role) =>
      ["CARRIER_OWNER", "CARRIER_DISPATCHER"].includes(role)
    )
  );

  const openApproval = (next: RefundLineApproval) => {
    setDetailId(null);
    setTimeout(() => setApproval(next), 0);
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        {error && rows.length > 0 && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
          >
            Refresh failed. Loaded cancellations remain available.
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <div
            role="status"
            aria-label="Loading cancellations"
            className="space-y-2 p-3"
          >
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
        ) : blockingError ? (
          <QueueMessage
            kind={hasHttpStatus(error, 403) ? "forbidden" : "error"}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <QueueMessage kind="empty" onRetry={() => void refetch()} />
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((row) => {
                const line = row.pendingProviderRefundLine;
                return (
                  <article
                    key={row.id}
                    className="relative rounded-md border p-3"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={`Review cancellation for SHP-${row.shipmentNumber}`}
                      className="absolute inset-0 z-0 h-full w-full rounded-md p-0"
                      onClick={() => setDetailId(row.id)}
                    />
                    <div className="pointer-events-none relative z-10 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-mono text-xs font-semibold">
                          SHP-{row.shipmentNumber}
                        </h3>
                        <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                          {row.order
                            ? `ORD-${row.order.orderNumber}`
                            : "No order"}
                          {row.trackingNumber ? ` · ${row.trackingNumber}` : ""}
                        </p>
                      </div>
                      <StatusBadge
                        status={line ? "REFUND_APPROVAL" : row.status}
                      />
                    </div>
                    <p className="pointer-events-none relative z-10 mt-2 break-words text-xs">
                      {customerName(row)}
                    </p>
                    <p className="pointer-events-none relative z-10 mt-2 line-clamp-2 text-xs text-muted-foreground">
                      {cancellationReason(row)}
                    </p>
                    <div className="pointer-events-none relative z-10 mt-2 flex flex-wrap justify-between gap-2 text-xs">
                      <span>
                        {formatDate(
                          row.cancellationRequestedAt ?? line?.createdAt
                        )}
                      </span>
                      <span className="font-medium">
                        {formatMoney(refundAmount(row))} CAD
                      </span>
                    </div>
                    {line && (
                      <div className="pointer-events-none relative z-10 mt-3 border-t pt-3">
                        <Button
                          type="button"
                          size="touch"
                          className="pointer-events-auto relative z-20 w-full"
                          aria-label={`Authorize provider component for SHP-${row.shipmentNumber}`}
                          onClick={() => {
                            const next = approvalFor(row);
                            if (next) openApproval(next);
                          }}
                        >
                          Authorize provider component
                        </Button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shipment</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="text-right">Refund</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const line = row.pendingProviderRefundLine;
                    return (
                      <TableRow key={row.id}>
                        <TableCell>
                          <div className="font-mono text-xs">
                            SHP-{row.shipmentNumber}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {row.order ? `ORD-${row.order.orderNumber}` : "-"}
                            {row.trackingNumber
                              ? ` · ${row.trackingNumber}`
                              : ""}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>{customerName(row)}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.order?.placerCustomer?.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={line ? "REFUND_APPROVAL" : row.status}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(
                            row.cancellationRequestedAt ?? line?.createdAt
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatMoney(refundAmount(row))}
                        </TableCell>
                        <TableCell className="max-w-64 truncate">
                          {cancellationReason(row)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              aria-label={`Review cancellation details for SHP-${row.shipmentNumber}`}
                              onClick={() => setDetailId(row.id)}
                            >
                              Review
                            </Button>
                            {line ? (
                              <Button
                                type="button"
                                size="sm"
                                aria-label={`Authorize provider component for SHP-${row.shipmentNumber}`}
                                onClick={() => {
                                  const next = approvalFor(row);
                                  if (next) openApproval(next);
                                }}
                              >
                                Authorize provider component
                              </Button>
                            ) : row.cancellationRequestedAt ? (
                              <span className="self-center text-xs text-muted-foreground">
                                Operational review
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {pagination && (
          <div className="border-t p-3">
            <TablePager
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              pageSize={pagination.limit}
              total={pagination.total}
              totalPages={Math.max(
                1,
                Math.ceil(pagination.total / pagination.limit)
              )}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      <CancellationDetail
        key={`cancellation-${detail?.id ?? "closed"}`}
        row={detail}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        onApprove={openApproval}
        canResolve={canResolve}
      />
      <ApproveRefundLineSheet
        key={approval?.lineId ?? "closed"}
        approval={approval}
        onOpenChange={(open) => {
          if (!open) setApproval(null);
        }}
      />
    </div>
  );
}
